import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PageHeader } from "../../../components/AthenaPricingEngine/page-header";
import { Panel } from "../../../components/AthenaPricingEngine/panel";
import { CredibilityTable } from "../../../components/AthenaPricingEngine/credibility-table";
import { KGovernanceCard } from "../../../components/AthenaPricingEngine/k-governance-card";
import { LossDevelopmentSection } from "../../../components/AthenaPricingEngine/loss-development-section";
import { Skeleton } from "../../../ui/skeleton";
import { hasTier } from "../../../components/AthenaPricingEngine/access-control";
import {
  recalibrate,
  useCalibrationSegments,
  useKGovernance,
  usePromoteK,
} from "../lib/api/calibration";

export default function Calibration() {
  const [searchParams] = useSearchParams();
  const segment = searchParams.get("segment") ?? undefined;
  const [sandboxK, setSandboxK] = useState("");

  const segmentsQuery = useCalibrationSegments();
  const governanceQuery = useKGovernance();
  const promote = usePromoteK();

  const parsedSandbox = Number(sandboxK);
  const liveK = governanceQuery.data?.liveK;
  const sandboxActive =
    sandboxK.trim() !== "" &&
    Number.isFinite(parsedSandbox) &&
    parsedSandbox > 0 &&
    parsedSandbox !== liveK;

  const rows = useMemo(() => {
    const base = segmentsQuery.data ?? [];
    if (!sandboxActive) return base;
    return base.map((row) => recalibrate(row, parsedSandbox)).sort((a, b) => a.z - b.z);
  }, [segmentsQuery.data, sandboxActive, parsedSandbox]);

  const matchedSegment = segment
    ? rows.find((row) => row.segmentId === segment)?.segmentId
    : undefined;

  useEffect(() => {
    if (!matchedSegment) return;
    const id = `segment-${matchedSegment.replace(/\s+/g, "-").toLowerCase()}`;
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [matchedSegment]);

  const isLoading = segmentsQuery.isLoading || governanceQuery.isLoading;
  const isError = segmentsQuery.isError || governanceQuery.isError;

  return (
    <div>
      <Link to="/risk-pricing-engine/segments" className="text-xs text-amber hover:underline">
        ← Back to Segments
      </Link>

      <PageHeader title="Loss Calibration" subtitle="How thin segments are pooled, and what k is set to" />

      {isError ? (
        <div
          role="alert"
          className="mt-8 border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          Could not load calibration data.
        </div>
      ) : isLoading || !governanceQuery.data ? (
        <div className="mt-8 space-y-2">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : (
        <>
          <LossDevelopmentSection />

          {/* This table is what K Governance below sets and what Loss Development above
              evidences, so it reads as supporting detail rather than the page's lead panel. */}
          <Panel
            className="mt-8"
            title="Segment Credibility"
            description={
              sandboxActive
                ? `Sandbox — not live · every Z and blended rate recomputed at k = ${parsedSandbox}`
                : "Sorted by own weight ascending, so the most-blended segments come first."
            }
            right={
              segment && !matchedSegment ? (
                <span className="text-xs text-muted-foreground">
                  No calibration row for “{segment}”
                </span>
              ) : null
            }
          >
            <CredibilityTable
              rows={rows}
              highlightSegmentId={matchedSegment}
              sandboxActive={sandboxActive}
            />
          </Panel>

          {/* Bottom of the page, on purpose: the governance action for a value everything
              above has already justified. */}
          <KGovernanceCard
            canPromote={hasTier("contributor")}
            governance={governanceQuery.data}
            sandboxK={sandboxK}
            onSandboxKChange={setSandboxK}
            isPromoting={promote.isPending}
            onPromote={async (k, note) => {
              await promote.mutateAsync({ k, note });
              setSandboxK("");
            }}
          />
        </>
      )}
    </div>
  );
}
